import React from 'react';
import { finiteNumber, formatValue } from '../format';

function rangeText(range, unit = '') {
  if (!range || range.available === false) return 'Not available';
  const low = finiteNumber(range.p05 ?? range.expected_low);
  const high = finiteNumber(range.p95 ?? range.expected_high);
  if (low === null || high === null) return 'Not available';
  return `${formatValue(low, unit)} – ${formatValue(high, unit)}`;
}

function affectedFor(row, affected) {
  return affected.get(row.key) || affected.get(row.metric);
}

function TelemetryRow({ row, affected }) {
  const value = finiteNumber(row.value);
  const issue = affectedFor(row, affected);
  const level = String(issue?.level || '').toUpperCase();
  const tone = value === null ? 'grey' : level === 'ALERT' ? 'red' : level === 'WARNING' ? 'amber' : 'green';
  const label = value === null ? 'Missing' : level ? String(issue?.direction || level).replaceAll('_', ' ') : 'Normal';
  const baseline = finiteNumber(row.range?.median);

  return (
    <tr>
      <td>
        <strong>{row.label}</strong>
      </td>
      <td className={value === null ? 'missing-cell' : ''}>
        {formatValue(value, row.unit)}
      </td>
      <td>{baseline === null ? 'Not available' : formatValue(baseline, row.unit)}</td>
      <td>{rangeText(row.range, row.unit)}</td>
      <td>
        <span className={`status-pill is-${tone}`}>{label}</span>
      </td>
    </tr>
  );
}

export function TelemetryTable({ payload }) {
  if (!payload) return null;
  const values = payload.current_values || {};
  const ranges = payload.baseline_ranges || {};
  const affected = new Map((payload.affected_readings || []).map((item) => [item.metric, item]));

  const rows = [
    { key: 'current', metric: 'current', label: 'Current average', value: values.current?.avg, unit: 'A', range: ranges.current?.avg },
    { key: 'voltage', metric: 'voltage', label: 'Voltage average', value: values.voltage?.avg, unit: 'V', range: ranges.voltage?.avg },
    { key: 'power', metric: 'power', label: 'Power', value: values.power?.total_kw, unit: 'kW', range: ranges.power?.total_kw },
    { key: 'pf', metric: 'pf', label: 'Power factor', value: values.pf?.avg, unit: '', range: ranges.pf?.avg },
    { key: 'flow', metric: 'flow', label: 'Flow', value: values.flow?.flowrate, unit: 'm³/h', range: ranges.flow?.flowrate },
    ...['r', 'y', 'b'].map((phase) => ({
      key: `current_${phase}`,
      metric: 'current',
      label: `${phase.toUpperCase()} phase current`,
      value: values.current?.[phase],
      unit: 'A',
      range: ranges.current?.[phase],
    })),
    ...['r', 'y', 'b'].map((phase) => ({
      key: `voltage_${phase}`,
      metric: 'voltage',
      label: `${phase.toUpperCase()} phase voltage`,
      value: values.voltage?.[phase],
      unit: 'V',
      range: ranges.voltage?.[phase],
    })),
    ...['r', 'y', 'b'].map((phase) => ({
      key: `pf_${phase}`,
      metric: 'pf',
      label: `${phase.toUpperCase()} phase PF`,
      value: values.pf?.[phase],
      unit: '',
      range: ranges.pf?.[phase],
    })),
  ];

  const monitored = rows.filter((row) => finiteNumber(row.value) !== null).length;

  return (
    <section className="card-panel telemetry-panel" aria-labelledby="telemetry-title">
      <div className="panel-heading">
        <div>
          <span className="panel-kicker">Live snapshot</span>
          <h2 id="telemetry-title">Telemetry Parameters</h2>
        </div>
        <span className="channel-badge">{monitored} monitored channels</span>
      </div>
      <div className="table-scroll">
        <table className="telemetry-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Current Value</th>
              <th>Baseline (Median)</th>
              <th>Expected Range (P05–P95)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <TelemetryRow key={row.key} row={row} affected={affected} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default TelemetryTable;
